import React, { useMemo } from 'react';
import type { Frame, MatOption } from '@shared/types/firestore';

interface FramePreviewProps {
  frame: Frame | null;
  matColor?: MatOption | null;
  frameOffset?: number;
  imageWidth: number;
  imageHeight: number;
  className?: string;
}

export default function FramePreview({ 
  frame, 
  matColor, 
  frameOffset = 0,
  imageWidth, 
  imageHeight, 
  className = '' 
}: FramePreviewProps) {
  // Calculate frame dimensions based on the image size and frame settings
  const frameStyles = useMemo(() => {
    if (!frame) return null;

    // Default frame dimensions (these would normally come from the frame data)
    const frameWidth = frame.offsetPixels || 40; // Default frame width in pixels
    const baseMatWidth = frame.matWidthPixels || 20;
    const matScale = Math.max(0, 2 + frameOffset) / 2;
    const matWidth = matColor ? baseMatWidth * matScale : 0;

    // Total border width is frame + mat
    const totalBorderWidth = frameWidth + matWidth;

    return {
      frameWidth,
      matWidth,
      totalBorderWidth,
      frameColor: frame.primaryColor || '#8B4513', // Default brown frame color
      matColorHex: matColor?.hexColor || '#FFFFFF'
    };
  }, [frame, frameOffset, matColor]);

  if (!frame || !frameStyles) {
    return null;
  }

  const { frameWidth, matWidth, totalBorderWidth, frameColor, matColorHex } = frameStyles;

  return (
    <div 
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{
        width: imageWidth + (totalBorderWidth * 2),
        height: imageHeight + (totalBorderWidth * 2),
        top: -totalBorderWidth,
        left: -totalBorderWidth,
      }}
    >
      {/* Outer Frame */}
      <div
        className="absolute inset-0 shadow-lg"
        style={{
          background: frameColor,
          borderRadius: frame.style === 'modern' ? '4px' : '0px'
        }}
      >
        {/* Inner Mat (if present) */}
        {matWidth > 0 && (
          <div
            className="absolute"
            style={{
              top: frameWidth,
              left: frameWidth,
              right: frameWidth,
              bottom: frameWidth,
              backgroundColor: matColorHex,
              borderRadius: frame.style === 'modern' ? '2px' : '0px'
            }}
          />
        )}
        
        {/* Image Cutout */}
        <div
          className="absolute bg-transparent"
          style={{
            top: totalBorderWidth,
            left: totalBorderWidth,
            width: imageWidth,
            height: imageHeight,
            borderRadius: frame.style === 'modern' ? '1px' : '0px'
          }}
        />
      </div>

      {/* Frame Pattern/Texture Overlay (if frame has a pattern) */}
      {frame.texturePattern && (
        <div
          className="absolute inset-0 opacity-30 mix-blend-multiply"
          style={{
            backgroundImage: `url(${frame.texturePattern})`,
            backgroundSize: '20px 20px',
            backgroundRepeat: 'repeat',
            borderRadius: frame.style === 'modern' ? '4px' : '0px'
          }}
        />
      )}

      {/* Frame Inner Shadow */}
      <div
        className="absolute opacity-20"
        style={{
          top: frameWidth - 2,
          left: frameWidth - 2,
          right: frameWidth - 2,
          bottom: frameWidth - 2,
          boxShadow: 'inset 0 0 8px rgba(0,0,0,0.3)',
          borderRadius: frame.style === 'modern' ? '3px' : '0px'
        }}
      />
    </div>
  );
}
